import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const EMPTY = {
  title: "", slug: "", excerpt: "", category: "Home Care", content: "",
  image: "", image_alt: "", seo_title: "", seo_description: "", published: true,
};

const inputCls = "w-full rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/50 focus:border-leaf";

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | post object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/posts", form);
        toast.success("Post created");
      } else {
        await api.put(`/admin/posts/${editing.id}`, form);
        toast.success("Post updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Post deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8" data-testid="post-editor">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl font-bold text-forest">{editing === "new" ? "New Post" : "Edit Post"}</h2>
          <button type="button" onClick={() => setEditing(null)} data-testid="post-editor-cancel" className="p-2 text-slate-400 hover:text-forest" aria-label="Cancel"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Title *</label>
            <input data-testid="post-title-input" className={inputCls} value={form.title} onChange={set("title")} required />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Slug (auto if blank)</label>
            <input data-testid="post-slug-input" className={inputCls} value={form.slug} onChange={set("slug")} placeholder="my-article-slug" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Category</label>
            <select data-testid="post-category-select" className={inputCls} value={form.category} onChange={set("category")}>
              <option>Home Care</option>
              <option>Office Health</option>
              <option>Seasonal</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Featured image URL</label>
            <input data-testid="post-image-input" className={inputCls} value={form.image} onChange={set("image")} placeholder="https://..." />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Image alt text</label>
            <input data-testid="post-image-alt-input" className={inputCls} value={form.image_alt} onChange={set("image_alt")} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Excerpt</label>
            <textarea data-testid="post-excerpt-input" rows={2} className={inputCls} value={form.excerpt} onChange={set("excerpt")} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">Content * (HTML: p, h2, ul/li, a)</label>
            <textarea data-testid="post-content-input" rows={12} className={`${inputCls} font-mono text-xs`} value={form.content} onChange={set("content")} required />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">SEO title</label>
            <input data-testid="post-seo-title-input" className={inputCls} value={form.seo_title} onChange={set("seo_title")} />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-forest/70 mb-1.5">SEO description</label>
            <input data-testid="post-seo-desc-input" className={inputCls} value={form.seo_description} onChange={set("seo_description")} />
          </div>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm font-medium text-forest">
          <input type="checkbox" data-testid="post-published-checkbox" checked={form.published} onChange={set("published")} className="w-4 h-4 accent-leaf" />
          Published (visible on the blog)
        </label>
        <button type="submit" data-testid="post-save-button" disabled={saving} className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3 font-display font-bold text-sm text-white hover:bg-forest-soft disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Post
        </button>
      </form>
    );
  }

  return (
    <div data-testid="admin-posts-panel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-bold text-forest">Blog Posts ({posts.length})</h2>
        <button onClick={openNew} data-testid="post-new-button" className="inline-flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 text-sm font-bold text-forest-deep hover:bg-mint transition-colors">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-leaf" />
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="rounded-2xl bg-white border border-forest/10 p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`post-row-${p.slug}`}>
              <div>
                <p className="font-display font-bold text-forest">{p.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {p.category} · {format(new Date(p.published_at), "MMM d, yyyy")} ·{" "}
                  <span className={p.published ? "text-leaf-hover font-semibold" : "text-slate-400"}>{p.published ? "Published" : "Draft"}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(p)} data-testid={`post-edit-${p.slug}`} className="p-2 text-slate-400 hover:text-forest" aria-label="Edit post"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.slug}`} className="p-2 text-slate-400 hover:text-red-600" aria-label="Delete post"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
